import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, Suspense, lazy } from "react";
import {flushSync} from "react-dom";
import Profile from "./Profile";
import MenuBar from "../components/MenuBar";
import Api from "../services/Api";
import Processor from "../services/Processor";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import Message from "../components/Message";
import Logo from "../components/Logo";
import useMediaQuery from '@mui/material/useMediaQuery';
import NoItems from "./NoItems";
import CurrencyApi from "../services/CurrencyApi";
import Auth from "../services/Auth";
import Confirm from "../components/Confirm";
import useReplaceSearchParams from "../services/SearchParam";
import { countries } from "../services/Countries";
import ItemList from "../components/ItemList";
import ItemView from "./ItemView";

// Search is only needed after user interaction, so keep it out of the initial bundle.
// ItemView remains synchronous so it can participate in the first shared-element snapshot.
const Search = lazy(() => import("./Search"));

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
let pageSize = 50;
let searchStop = false;
let currencyApi = new CurrencyApi();
let controller = new AbortController();
let preLoadController = new AbortController();
let allItems = [];
let lastItemMap = {};
let auth;
let maxApiDepthMap = {};
const version = "1.0.14";

function enableInteractionDuringTransition(transition) {
    const handlePointerDown = (event) => {
        const { clientX, clientY } = event;
        event.preventDefault();
        event.stopImmediatePropagation();
        transition.skipTransition();

        requestAnimationFrame(() => {
            const target = document.elementFromPoint(clientX, clientY);
            const interactiveTarget = target?.closest?.(
                'button, a, input, textarea, select, [role="button"], [tabindex]'
            );
            if (interactiveTarget && !interactiveTarget.disabled) {
                interactiveTarget.click();
                return;
            }

            target?.closest?.(".MuiImageListItem-root")?.dispatchEvent(new PointerEvent("pointerup", {
                bubbles: true,
                button: event.button,
                clientX,
                clientY,
                pointerType: event.pointerType
            }));
        });
    };

    document.addEventListener("pointerdown", handlePointerDown, { capture: true });
    transition.finished.finally(() => {
        document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
    });
}

function createOpeningTransitionSource(source, viewportBounds) {
    if (!(source instanceof HTMLImageElement) || !viewportBounds) return null;
    const sourceBounds = source.getBoundingClientRect();
    const frame = document.createElement("div");
    Object.assign(frame.style, {
        position: "fixed",
        left: `${viewportBounds.left}px`,
        top: `${viewportBounds.top}px`,
        width: `${viewportBounds.width}px`,
        height: `${viewportBounds.height}px`,
        overflow: "hidden",
        pointerEvents: "none"
    });
    const snapshot = source.cloneNode();
    Object.assign(snapshot.style, {
        position: "absolute",
        left: `${sourceBounds.left - viewportBounds.left}px`,
        top: `${sourceBounds.top - viewportBounds.top}px`,
        width: `${sourceBounds.width}px`,
        height: `${sourceBounds.height}px`,
        maxWidth: "none",
        maxHeight: "none",
        transform: "none",
        transition: "none"
    });
    frame.appendChild(snapshot);
    document.body.appendChild(frame);
    return { frame, snapshot };
}

export default function Root() {
    const [searchParams, setSearchParams] = useReplaceSearchParams();
    const [preferredEbay, setPreferredEbay] = React.useState('ca');
    const [open, setOpen] = React.useState(false);
    const openRef = useRef(open);
    openRef.current = open;
    const [openMessage, setOpenMessage] = React.useState(false);
    const [openConfirm, setOpenConfirm] = React.useState(false);
    const [message, setMessage] = React.useState({ message: null, severity: null });
    const [messageButton, setMessageButton] = React.useState('');
    const [secondaryAction, setSecondaryAction] = React.useState(null);
    const [secondaryActionButton, setSecondaryActionButton] = React.useState('');
    const [confirmMessage, setConfirmMessage] = React.useState('');
    const [confirmAction, setConfirmAction] = React.useState('');
    const [confirmTitle, setConfirmTitle] = React.useState('Confirm');
    const [id, setID] = React.useState(0);
    const [currentItem, setCurrentItem] = React.useState(null);
    const [listingImageSelections, setListingImageSelections] = useState({});
    const [activeListingImageSelection, setActiveListingImageSelection] = useState(null);
    const listingImageSelectionsRef = useRef({});
    const [items, setItems] = useState([]);
    const [filterSellers, setFilterSellers] = useState([]);
    const [filterCountries, setFilterCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totals, setTotals] = useState(0);
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
    const [user, setUser] = useState(null);
    const [blockedSellers, setBlockedSellers] = useState([]);
    const [blockedCountries, setBlockedCountries] = useState([]);
    const [recentItems, setRecentItems] = useState([]);
    const [savedSearches, setSavedSearches] = useState([]);
    const [favoriteSearchIndex, setFavoriteSearchIndex] = useState(null);
    const [loadingUserData, setLoadingUserData] = useState(true);
    const [signingIn, setSigningIn] = useState(false);
    const [subscriptions, setSubscriptions] = useState([]);
    const [showQuickScroll, setShowQuickScroll] = useState(false);
    const [enableQuickScroll, setEnableQuickScroll] = useState(false);
    const [leftHanded, setLeftHanded] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [enableShowFirstItem, setEnableShowFirstItem] = useState(true);
    const [value, setValue] = React.useState(0);
    const disableProfileButton = user === null;
    const [keywords, setKeywords] = React.useState('');
    const [editing, setEditing] = useState(false);
    const [disabledSearch, setDisabledSearch] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);
    const [firstItemRefresh, setFirstItemRefresh] = useState(null);
    const [currentOverlayId, setCurrentOverlayId] = useState(0);
    const sort = searchParams.get('sort');
    const [hasUpdate, setHasUpdate] = useState(false);
    const [versionInfo, setVersionInfo] = useState(null);
    const listingTransitionSource = useRef(null);
    const listingTransition = useRef(null);
    const listingCloseInProgress = useRef(false);

    useEffect(() => {
        const getVersion = async () => {
            try {
                const v = await Api.fetchVersion();
                if (!v?.number) return;
                setHasUpdate(v?.number !== version);
                setVersionInfo(v);
            } catch (e) { }
        }
        getVersion();
    }, []);

    useEffect(() => {
        auth = new Auth(setUser);
    }, [])

    useLayoutEffect(() => {
        if (!loadingUserData) {
            if (searchParams.has('keywords')) {
                setPreferredEbay(searchParams.get('prefer'));
                handleSearch();
            }
        }
    }, [loadingUserData, searchParams])

    useEffect(() => {
        if (user && !signingIn) {
            setLoadingUserData(true);
            handleUserData(true);
        } else if (user === false) {
            setLoadingUserData(false);
        }
    }, [user, signingIn])

    useLayoutEffect(() => {
        if (!(open && id === 1) && !searchParams.has('keywords') && savedSearches.length > 0 && favoriteSearchIndex !== null &&
            savedSearches.length - 1 >= favoriteSearchIndex) {
            setSearchParams(new URLSearchParams(savedSearches[favoriteSearchIndex]));
            if (loadingUserData) {
                setPreferredEbay(searchParams.get('prefer'));
                window.history.forward();
                handleSearch();
            }
        }
    }, [favoriteSearchIndex])

    const reset = useCallback((isRefresh) => {
        if (!isRefresh) {
            setItems([]);
            setListingImageSelections({});
            listingImageSelectionsRef.current = {};
        }
        setFirstItemRefresh(null);
        setCurrentOverlayId(0);
        allItems = []
        lastItemMap = {};
        setLoading(false);
        maxApiDepthMap = {};
    }, []);

    function showConfirm(message, messageButton, action, title, secondaryAction, secondaryMessageButton) {
        setMessageButton(messageButton);
        setSecondaryActionButton(secondaryMessageButton);
        setSecondaryAction(() => secondaryAction);
        setConfirmMessage(message);
        setConfirmAction(() => action);
        setConfirmTitle(title ?? "Confirm");
        setOpenConfirm(true);
    }

    const showMessage = useCallback((message, severity, search, searchRefresh, additional) => {
        setMessage({
            message: message,
            severity: severity,
            search: search,
            searchRefresh: searchRefresh,
            key: new Date().getTime(),
            additional: additional
        });
        setOpenMessage(true);
    }, [])

    function showNoItemsMessage() {
        reset();
        showMessage(`No items found`, 'info', true);
    }

    const handleLogout = useCallback(() => {
        function resetOnLogout() {
            setFavoriteSearchIndex(null);
            setBlockedSellers([]);
            setBlockedCountries([]);
            setRecentItems([]);
            setSavedSearches([]);
            setSearchParams('');
            reset();
        }

        showConfirm('Are you sure you want to logout?', 'Logout', () => {
            auth.logout();
            resetOnLogout();
            showMessage('Logged out');
        }, "Logout")
    }, []);

    const handleUserData = useCallback((isLaunch, isSilent) => {
        auth.getUserData().then((d) => {
            setLoadingUserData(false);
            setBlockedSellers(d.blockedSellers ? d.blockedSellers : []);
            setBlockedCountries(d.blockedCountries ? d.blockedCountries : []);
            setRecentItems(prev => {
                const set = new Set([...(d.recentItems ?? []), ...(prev ?? [])])
                return [...set];
            });
            setSavedSearches(d.savedSearches ? d.savedSearches : []);
            setFavoriteSearchIndex(d.favoriteSearchIndex);
        }).catch((e) => {
            if (!isSilent) showMessage('Authentication error: We are unable to reach our servers', 'error');
            setLoadingUserData(false);
        });
        auth.getUserSubscriptions().then((d) => {
            setSubscriptions(d);
        }).catch((e) => {
            if (!isSilent) showMessage('Authentication error: We are unable to reach our servers', 'error');
            setLoadingUserData(false);
        });
        if (isLaunch) auth.updateUserData({ timeZone: timeZone });
    }, [auth]);

    const updateUser = useCallback((data, setLoading, successMessage, message, messageButton, query, title, oldQuery, newKeywords, itemIndex) => {
        function updateUserSubscriptionOnDelete(query) {
            if (subscriptions.length === 0) return;
            const exists = subscriptions.find((sub) => sub.query === query);
            if (exists && !exists.isDisabled) {
                const newData = { ...exists, isDisabled: true, cachedItems: [] };
                auth.updateUserSubscriptions(newData);
            }
        }

        async function updateUserSearch() {
            const oldQueryParam = new URLSearchParams(oldQuery);
            oldQueryParam.set("keywords", newKeywords);
            if (savedSearches.includes(oldQueryParam.toString())) {
                if (setLoading) {
                    setLoading(false);
                }
                showMessage("Search already exists", "warning");
                return;
            }
            const exists = subscriptions.find((sub) => sub.query === oldQuery);
            if (exists && !exists.isDisabled) {
                const newData = { ...exists, query: oldQueryParam.toString() };
                await auth.updateUserSubscriptions(newData);
            }
            savedSearches[itemIndex] = oldQueryParam.toString();
            auth.updateUserData({ savedSearches: savedSearches }).then((d) => {
                if (setLoading) {
                    setLoading(false);
                }
                showMessage(successMessage);
                handleUserData();
            });
        }

        let action = () => {
            if (setLoading) {
                setLoading(true);
            }
            if (query) {
                try {
                    updateUserSubscriptionOnDelete(query);
                } catch {
                    showMessage("Database error", 'error');
                    return;
                }
            }
            if (oldQuery || newKeywords || itemIndex) {
                try {
                    updateUserSearch();
                } catch {
                    if (setLoading) {
                        setLoading(false);
                    }
                    showMessage("Database error", 'error');
                    return;
                }
                return;
            }
            try {
                auth.updateUserData(data).then((d) => {
                    if (setLoading) {
                        setLoading(false);
                    }
                    showMessage(successMessage);
                    handleUserData()
                });
            } catch (e) {
                if (setLoading) {
                    setLoading(false);
                }
                showMessage("Database error", 'error');
            }
        };
        if (auth && user) {
            if (!message) {
                action();
            } else {
                showConfirm(message, messageButton, action, title);
            }
        }
    }, [subscriptions, user, auth, savedSearches])

    const getItems = useCallback(async (entries = 100, countryCode, page, next) => {
        if (!countryCode) {
            controller.abort();
            controller = new AbortController();
        }
        const recents = JSON.parse(localStorage.getItem("recent") ?? "[]") ?? [];
        const params = searchParams.toString();
        const index = recents.findIndex((r) => r === params);
        if (index > -1) recents.splice(index, 1);
        recents.unshift(params);
        localStorage.setItem("recent", JSON.stringify(recents));
        let keywords = searchParams.get('keywords');
        let category = searchParams.get('category');
        let sort = searchParams.get('sort');
        let minPrice = searchParams.get('minPrice');
        let maxPrice = searchParams.get('maxPrice');
        let currency = searchParams.get('currency');
        let sellers = searchParams.get('blockedSellers');
        let countries = searchParams.get('blockedCountries');
        let vBlockedCountries = countries === 'false' ? [] : blockedCountries;
        let vBlockedSellers = sellers === 'false' ? [] : blockedSellers;
        let listingTypes = searchParams.getAll('listingTypes');
        currencyApi.setCurrency(currency ? currency : 'cad');
        let data;
        try {
            data = await Api.fetchItems(auth, keywords, category, sort, page ?? 0, entries, minPrice, maxPrice, listingTypes,
                currency, vBlockedSellers, vBlockedCountries, controller, countryCode, undefined, next);
        } catch (e) {
            if (e.name === "AbortError") return -1;
            if (Object.keys(e).length) {
                showMessage("Server error: We can't reach our servers right now, try again later", 'error', undefined, undefined,
                    JSON.stringify(e));
            }
            throw 'Server error';
        }
        if (!data || data?.errors) {
            let message = "We can't reach eBay right now, try again later";
            showMessage("eBay error: " + message, 'error');
            throw 'eBay Server error';
        }
        // if (blockedSellers.length > 0 && data[0].ack[0] === 'Failure' && data[0].errorMessage[0].error[0].errorId[0] === '28') {
        //     let errorSellers = data[0].errorMessage[0].error[0].parameter;
        //     let message = (`Invalid seller${errorSellers.length === 1 ? "" : "s"} ` + errorSellers.join(', ') + " removed from blocked sellers");
        //     setItems([]);
        //     updateUser({blockedSellers: blockedSellers.filter(i => !errorSellers.includes(i))}, setLoading, message);
        //     throw 'Invalid seller error';
        // }
        // if (data.length === 0) {
        //     showMessage("Search error: Try different search parameters", 'error');
        //     throw 'Search error';
        // }
        if (!data?.itemSummaries) {
            showNoItemsMessage();
            throw 'No results';
        }
        if (data.lastItemMap) lastItemMap = { ...lastItemMap, ...data.lastItemMap };
        data = data.itemSummaries;
        return data;
    }, [searchParams, blockedCountries, blockedSellers, updateUser])

    const handleSearch = useCallback(async (isRefresh) => {
        reset(isRefresh)
        setLoading(true);
        searchStop = false;
        let data;
        try {
            data = await getItems();
        } catch (e) {
            setLoading(false);
            return;
        }
        if (data === -1) return;

        function updateUserSubscriptionOnSearch(data) {
            if (subscriptions.length === 0) return;
            const exists = subscriptions.find((sub) => sub.query === searchParams.toString());
            if (exists && !exists.isDisabled) {
                const idsToSave = data.map((item) => item.itemId);
                const newData = { ...exists, cachedItems: idsToSave, isNew: false };
                try {
                    auth.updateUserSubscriptions(newData);
                } catch {
                    showMessage("Database error", 'error');
                }
            }
        }

        function checkIfSameFirstItem() {
            if (items.length > 0) {
                let firstItem = items[0];
                if (firstItem.itemId === allItems[0].itemId) {
                    setLoading(false);
                }
            }
        }

        updateUserSubscriptionOnSearch(data);
        allItems = await Processor.convertPrices(data, currencyApi);
        if (allItems.length === 0) {
            showNoItemsMessage();
        } else {
            checkIfSameFirstItem();
        }
        setTotals(allItems.length);
        setItems((prev) => {
            if (new URLSearchParams(window.location.search).toString() === '') {
                return [];
            }
            const nextItems = paginate(allItems, pageSize, 1);
            if (isRefresh && prev.length > 0 && enableShowFirstItem && sort === 'StartTimeNewest' && nextItems.length > 0) {
                const itemIndex = nextItems.findIndex(item1 => {
                    const indexInArray2 = prev.findIndex(item2 => item1.itemId === item2.itemId);
                    return indexInArray2 !== -1;
                });
                const item = nextItems[itemIndex];
                if (item) {
                    setFirstItemRefresh(item);
                    showMessage(itemIndex === 0 ? "No new items" : `${itemIndex} new item${itemIndex === 1 ? "" : "s"}`, "info", false, true)
                } else {
                    showMessage("50+ new items", "info", false, true)
                }
            }
            return nextItems
        });
        setLoading(false);
    }, [getItems, sort, enableShowFirstItem]);

    const refreshClick = useCallback(() => {
        if (!loading) {
            handleSearch(true);
        }
    }, [handleSearch, loading])

    const updateUserSubscription = useCallback((data, setLoading, successMessage, message, messageButton) => {
        let action = () => {
            if (setLoading) {
                setLoading(true);
            }
            const exists = subscriptions.find((sub) => sub.query === data);
            let newData = {};
            if (exists) {
                newData = { ...exists, isDisabled: !exists.isDisabled, isNew: true, cachedItems: [], notifiedItems: [] }
            } else {
                newData = { query: data, isNew: true, isDisabled: false, cachedItems: [], notifiedItems: [] }
            }
            try {
                auth.updateUserSubscriptions(newData).then((d) => {
                    if (setLoading) {
                        setLoading(false);
                    }
                    showMessage(successMessage);
                    handleUserData()
                });
            } catch (e) {
                if (setLoading) {
                    setLoading(false);
                }
                showMessage("Database error", 'error');
            }
        };
        if (auth && user) {
            if (!message) {
                action();
            } else {
                showConfirm(message, messageButton, action);
            }
        }
    }, [subscriptions, auth, user])

    const appendUserArray = useCallback((data, setLoading, successMessage, handleSearch, firstClose, message, messageButton, title, filterSeller, isCountry) => {
        if (auth && user) {
            const action = () => {
                if (firstClose) {
                    handleClose();
                }
                if (setLoading) {
                    setLoading(true);
                }
                if (handleSearch) {
                    handleSearch();
                }
                try {
                    auth.appendUserArray(data).then(() => {
                        if (setLoading) {
                            setLoading(false);
                        }
                        showMessage(successMessage);
                        handleUserData();
                        if (!firstClose) {
                            handleClose();
                        }
                    });
                } catch (e) {
                    if (setLoading) {
                        setLoading(false);
                    }
                    showMessage("Database error", 'error');
                }
            }
            let additionalMessage = '';
            if (filterSeller) {
                const length = isCountry ? allItems?.filter((item) => item.itemLocation?.country === filterSeller)?.length :
                    allItems?.filter((item) => item.seller?.username === filterSeller)?.length;
                if (length) additionalMessage = ` (${length} listing${length === 1 ? "" : "s"})`;
            }
            if (filterSeller && (isCountry ? blockedSellers.includes(filterSeller) : blockedCountries.includes(filterSeller))) {
                handleClose();
                showMessage(`'${isCountry ? countries[filterSeller] : filterSeller}' already blocked`, "info");
                return;
            }
            if (filterSeller && (isCountry ? !filterCountries.includes(filterSeller) : !filterSellers.includes(filterSeller))) {
                const secondaryAction = () => {
                    if (isCountry) {
                        setFilterCountries(prev => [...prev, filterSeller]);
                    } else {
                        setFilterSellers(prev => [...prev, filterSeller]);
                    }
                    handleClose();
                    showMessage(`'${isCountry ? countries[filterSeller] : filterSeller}' soft blocked for this session`);
                };
                showConfirm(message + additionalMessage, messageButton, action, title, secondaryAction,
                    "Soft block");
                return;
            }
            showConfirm(message, messageButton, action, title);
        }
        // `allItems` is a module-level variable read live at call time, so it is
        // intentionally omitted from the deps: including it only changed this
        // callback's identity on every search and needlessly re-rendered the grid.
    }, [handleUserData, auth, user, filterSellers, filterCountries, blockedSellers, blockedCountries])

    async function getMoreItems() {
        const totalPages = Math.ceil(allItems.length / pageSize);
        let currentPage = Math.floor(items.length / pageSize);
        if (totalPages > currentPage && window.scrollY !== 0 && !loading) {
            currentPage++;
            setItems((currentItems) => {
                if (new URLSearchParams(window.location.search).toString() === '') {
                    return [];
                }
                let next = ([...currentItems, ...paginate(allItems, pageSize, currentPage)]);
                next = Processor.removeDuplicatesFromItems(next);
                return next
            }
            );
        }
    }

    useBottomScrollListener(getMoreItems, {
        offset: 1250,
        debounce: 1000
    })

    const handleToggle = useCallback((nextId, item, state, transitionSource) => {
        const update = () => {
            setOpen((currentOpen) => state ?? !currentOpen);
            setID(nextId);
            if (item) {
                setCurrentItem(item);
                setActiveListingImageSelection(listingImageSelectionsRef.current[item.itemId] ?? null);
            }
        };
        const canTransition = nextId === 2 && transitionSource instanceof HTMLImageElement &&
            document.startViewTransition;
        if (!canTransition) {
            update();
            return;
        }

        const sourceBounds = transitionSource.getBoundingClientRect();
        const viewportBounds = transitionSource.parentElement?.getBoundingClientRect();
        if (viewportBounds && sourceBounds.width && sourceBounds.height) {
            const clipTop = Math.max(0, viewportBounds.top - sourceBounds.top) / sourceBounds.height * 100;
            const clipRight = Math.max(0, sourceBounds.right - viewportBounds.right) / sourceBounds.width * 100;
            const clipBottom = Math.max(0, sourceBounds.bottom - viewportBounds.bottom) / sourceBounds.height * 100;
            const clipLeft = Math.max(0, viewportBounds.left - sourceBounds.left) / sourceBounds.width * 100;
            document.documentElement.style.setProperty(
                "--listing-source-clip",
                `${clipTop}% ${clipRight}% ${clipBottom}% ${clipLeft}%`
            );
        }
        listingTransitionSource.current?.closest(".ListingGridItem")?.classList.remove("CurrentListingGridItem");
        listingTransitionSource.current = transitionSource;
        transitionSource.closest(".ListingGridItem")?.classList.add("CurrentListingGridItem");
        transitionSource.closest(".ListingGridItem")?.classList.add("PreparingListingTransition");
        document.documentElement.classList.add("listing-opening");
        requestAnimationFrame(() => {
            const openingSource = createOpeningTransitionSource(transitionSource, viewportBounds);
            const snapshotSource = openingSource?.snapshot ?? transitionSource;
            snapshotSource.style.viewTransitionName = "ebay-listing-photo";
            const transition = document.startViewTransition(() => {
                snapshotSource.style.viewTransitionName = "none";
                flushSync(update);
                openingSource?.frame.remove();
            });
            listingTransition.current = transition;
            enableInteractionDuringTransition(transition);
            transition.finished.finally(() => {
                snapshotSource.style.viewTransitionName = "";
                openingSource?.frame.remove();
                transitionSource.closest(".ListingGridItem")?.classList.remove("PreparingListingTransition");
                document.documentElement.classList.remove("listing-opening");
                if (listingTransition.current === transition) listingTransition.current = null;
            });
        });
    }, [])
    const [animationComplete, setAnimationComplete] = useState("complete");

    const handleTransitionEnd = useCallback(() => {
        setAnimationComplete("complete");
    }, []);

    const handleListingImageChange = useCallback((itemId, imageUrl, index) => {
        if (!itemId || !imageUrl) return;
        const currentSelection = listingImageSelectionsRef.current[itemId];
        if (currentSelection?.imageUrl === imageUrl && currentSelection?.index === index) return;

        const nextSelection = { imageUrl, index };
        listingImageSelectionsRef.current = {
            ...listingImageSelectionsRef.current,
            [itemId]: nextSelection
        };
        React.startTransition(() => {
            setListingImageSelections((selections) => ({
                ...selections,
                [itemId]: nextSelection
            }));
        });
    }, []);

    const handleClose = useCallback((fromSearch) => {
        if (listingCloseInProgress.current) return;

        const update = () => {
            setOpen(false);
            setPreload(null);

            if (fromSearch === true) {
                setEditing(false);
                setAnimationComplete("start");
            }
        };
        const transitionSource = listingTransitionSource.current;
        const canTransition = id === 2 && open && transitionSource?.isConnected &&
            document.startViewTransition;
        if (!canTransition) {
            transitionSource?.closest(".ListingGridItem")?.classList.remove("CurrentListingGridItem");
            update();
            return;
        }

        listingCloseInProgress.current = true;
        const closeWithTransition = () => {
            const sourceBounds = transitionSource.getBoundingClientRect();
            const viewportBounds = transitionSource.parentElement?.getBoundingClientRect();
            const transitionDestination = transitionSource.closest(".ListingImageViewport") ?? transitionSource;
            const sourceFilter = transitionSource.style.filter || getComputedStyle(transitionSource).filter;
            document.documentElement.style.setProperty(
                "--listing-source-filter",
                sourceFilter === "none" ? "brightness(1)" : sourceFilter
            );
            if (viewportBounds && sourceBounds.width && sourceBounds.height) {
                const clipTop = Math.max(0, viewportBounds.top - sourceBounds.top) / sourceBounds.height * 100;
                const clipRight = Math.max(0, sourceBounds.right - viewportBounds.right) / sourceBounds.width * 100;
                const clipBottom = Math.max(0, sourceBounds.bottom - viewportBounds.bottom) / sourceBounds.height * 100;
                const clipLeft = Math.max(0, viewportBounds.left - sourceBounds.left) / sourceBounds.width * 100;
                document.documentElement.style.setProperty(
                    "--listing-source-clip",
                    `${clipTop}% ${clipRight}% ${clipBottom}% ${clipLeft}%`
                );
            }
            transitionSource.closest(".ListingGridItem")?.classList.add("PreparingListingTransition");
            document.documentElement.classList.add("listing-closing");
            const transition = document.startViewTransition(() => {
                transitionSource.style.visibility = "visible";
                transitionDestination.style.viewTransitionName = "ebay-listing-photo";
                flushSync(update);
            });
            listingTransition.current = transition;
            enableInteractionDuringTransition(transition);
            transition.finished.finally(() => {
                transitionDestination.style.viewTransitionName = "";
                transitionSource.style.visibility = "";
                document.documentElement.classList.remove("listing-closing");
                document.documentElement.style.removeProperty("--listing-source-clip");
                document.documentElement.style.removeProperty("--listing-source-filter");
                transitionSource.closest(".ListingGridItem")?.classList.remove("PreparingListingTransition");
                transitionSource.closest(".ListingGridItem")?.classList.remove("CurrentListingGridItem");
                listingTransitionSource.current = null;
                if (listingTransition.current === transition) listingTransition.current = null;
                listingCloseInProgress.current = false;
            });
        };

        const activeTransition = listingTransition.current;
        if (activeTransition) {
            activeTransition.skipTransition();
            activeTransition.finished.finally(closeWithTransition);
        } else {
            closeWithTransition();
        }
    }, [id, open]);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
    }, []);

    const handleCloseMessage = useCallback((event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenMessage(false);
    }, []);

    function paginate(items, pageSize, pageNumber) {
        return items.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
    }

    const [preload, setPreload] = useState(null);

    const handlePreLoad = useCallback(async (id) => {
        preLoadController.abort();
        preLoadController = new AbortController();
        let data;
        try {
            data = await Api.fetchItem(auth, id, preLoadController);
        } catch (e) {
            return;
        }
        let out = { id: id };
        if ('image' in data) {
            out['image'] = data.image;
        }
        if ('additionalImages' in data) {
            out['images'] = data.additionalImages;
        }
        if (data.description) {
            out['description'] = data.description;
        }
        setPreload(out);
    }, []);
    const sellersDisabled = searchParams.get('blockedSellers') === 'false';
    const countriesDisabled = searchParams.get('blockedCountries') === 'false';
    const alwaysShow = searchParams.get('alwaysOverlay') === 'true';

    useEffect(() => {
        const showQuickScroll = localStorage.getItem("showQuickScroll");
        const leftHanded = localStorage.getItem("leftHanded");
        const enableQuickScroll = localStorage.getItem("enableQuickScroll");
        const enableAutoRefresh = localStorage.getItem("enableAutoRefresh");
        const enableFirstItem = localStorage.getItem("enableFirstItem");
        const justUpdated = localStorage.getItem("justUpdated");
        setShowQuickScroll(showQuickScroll === 'true');
        setEnableQuickScroll(enableQuickScroll === 'true');
        setLeftHanded(leftHanded === 'true');
        setAutoRefresh(enableAutoRefresh === 'true');
        setEnableShowFirstItem(!enableFirstItem || enableFirstItem === 'false');
    }, []);

    async function handleNextApiPage(countryCode, page) {
        let sort = searchParams.get('sort');
        let data;
        try {
            data = await getItems(100, countryCode, page, true);
        } catch (e) {
            searchStop = true;
            setLoading(false);
            return;
        }
        if (data === -1) return;
        const lastItem = items[items.length - 1];
        const index = allItems.findIndex(item => item.itemId === lastItem.itemId);
        allItems.push(...data);
        allItems = Processor.removeDuplicatesFromItems(allItems);
        let itemsToSort = allItems.splice(index);
        itemsToSort = Processor.removeDuplicatesFromItems(itemsToSort, sort, true);
        allItems.push(...itemsToSort);
        allItems = await Processor.convertPrices(allItems, currencyApi);
        setItems((prev) => {
            if (new URLSearchParams(window.location.search).toString() === '') {
                return [];
            }
            return allItems.slice(0, prev.length)
        });
        setTotals(allItems.length);
        setLoading(false);
    }

    useEffect(() => {
        if (!items || items.length === 0) return;
        const keyArray = [];
        if (searchParams.toString() === "") return;
        for (const key in lastItemMap) {
            if (lastItemMap.hasOwnProperty(key)) {
                if (!lastItemMap[key].lastId) {
                    if (maxApiDepthMap[key] > 5) continue;
                    maxApiDepthMap[key] = maxApiDepthMap[key] ? maxApiDepthMap[key] + 1 : 1;
                    keyArray.push({ key: key, value: lastItemMap[key] });
                }
            }
        }
        if (keyArray.length > 0) {
            keyArray.forEach((obj) => {
                if (obj.value.nextPage) {
                    handleNextApiPage(obj.key, obj.value.nextPage);
                    delete (lastItemMap[obj.key]);
                }
            })
        }
    }, [lastItemMap, searchParams, items]);

    useEffect(() => {
        if (!items || items.length === 0) return;
        if (searchParams.toString() === "") return;

        function findPairByLastIdAndFetch(data, someId) {
            const keyArray = [];
            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    if (data[key].lastId === someId) {
                        maxApiDepthMap[key] = 0;
                        keyArray.push({ key: key, value: data[key] });
                    }
                }
            }
            if (keyArray.length > 0) {
                keyArray.forEach((obj) => {
                    if (obj.value.nextPage) {
                        handleNextApiPage(obj.key, obj.value.nextPage);
                        delete (lastItemMap[obj.key]);
                    }
                })
            }
            return keyArray;
        }

        items.forEach((item) => {
            findPairByLastIdAndFetch(lastItemMap, item.itemId);
        })
    }, [items, lastItemMap, searchParams]);

    const goHome = useCallback(() => {
        setSearchParams();
        reset();
        controller.abort();
        controller = new AbortController();
    }, [reset]);

    useEffect(() => {
        if (!loading) {
            const element = document.getElementById("FirstItem");
            if (element) {
                let offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--sat"));
                if (isNaN(offset)) offset = 0;
                const y = element.offsetTop - offset - (2 * element.clientHeight) - 4;
                window.scrollTo({ top: y, behavior: "instant" })
            } else {
                window.scrollTo({ top: 0, behavior: "instant" })
            }
        }
    }, [loading]);

    return (
        <>
            <Logo user={!!user} goHome={goHome} total={totals} items={items.length}
                loading={loading || loadingUserData} />
            <MenuBar hasUpdate={hasUpdate} openMessage={openMessage} animationComplete={animationComplete}
                setAnimationComplete={setAnimationComplete}
                handleTransitionEnd={handleTransitionEnd} loadingUser={loadingUserData && !open}
                loading={loading} showSpinner={showSpinner}
                disabledSearch={disabledSearch} editing={editing} setEditing={setEditing}
                searchParams={searchParams} setKeywords={setKeywords}
                keywords={keywords} disableProfileButton={disableProfileButton} setValue={setValue}
                type={searchParams.getAll('listingTypes')} sort={sort} user={user} open={open}
                handleToggle={handleToggle}
                refreshClick={refreshClick} />
            <Profile changelog={versionInfo?.changelog} version={version} hasUpdate={hasUpdate} handleUserData={handleUserData} auth={auth} recentItems={recentItems} value={value} setEnableQuickScroll={setEnableQuickScroll}
                enableQuickScroll={enableQuickScroll} enableShowFirstItem={enableShowFirstItem}
                setValue={setValue} leftHanded={leftHanded} setLeftHanded={setLeftHanded}
                autoRefresh={autoRefresh} setAutoRefresh={setAutoRefresh}
                setEnableFirstItem={setEnableShowFirstItem}
                setShowQuickScroll={setShowQuickScroll} showQuickScroll={showQuickScroll}
                subscriptions={subscriptions} searchParams={searchParams}
                setSearchParams={setSearchParams} updateUser={updateUser}
                updateUserSubscription={updateUserSubscription}
                blockedSellers={blockedSellers} blockedCountries={blockedCountries}
                favoriteSearchIndex={favoriteSearchIndex}
                savedSearches={savedSearches} loadingUserData={loadingUserData}
                user={user} open={id === 0 && open} preferredEbay={preferredEbay}
                handleClose={handleClose} handleLogin={auth ? (isMicrosoft) => {
                    auth.signInWithGoogle(setSigningIn, isMicrosoft, showMessage)
                } : undefined}
                handleLogout={auth ? handleLogout : undefined} />
            {id === 1 &&
                <Suspense fallback={null}>
                    <Search setShowSpinner={setShowSpinner} setLoadingCategories={setLoadingCategories}
                        loadingCategories={loadingCategories} setDisabledSearch={setDisabledSearch}
                        keywords={keywords} setKeywords={setKeywords} auth={auth} showConfirm={showConfirm}
                        updateSearch={appendUserArray} savedSearches={savedSearches} user={user} open={open}
                        handleClose={handleClose} searchParams={searchParams}
                        setSearchParams={setSearchParams} />
                </Suspense>}
            {id === 2 &&
                <ItemView blockedSellers={blockedSellers} blockedCountries={blockedCountries}
                    isUser={!!user} sellersDisabled={sellersDisabled} countriesDisabled={countriesDisabled}
                    auth={auth}
                    preload={preload} updateUser={appendUserArray} open={open} handleClose={handleClose}
                    item={currentItem}
                    imageSelection={activeListingImageSelection}
                    onImageChange={handleListingImageChange}
                    preferredEbay={preferredEbay} sort={sort} setRecentItems={setRecentItems}
                    isRecent={recentItems?.includes?.(currentItem?.itemId)} />}
            <Confirm secondaryAction={secondaryAction} secondaryActionButton={secondaryActionButton}
                title={confirmTitle} open={openConfirm} handleClose={handleCloseConfirm}
                message={confirmMessage}
                action={confirmAction} messageButton={messageButton} />

            <Message open={openMessage} handleClose={handleCloseMessage} message={message} />
            <ItemList currentOverlayId={currentOverlayId} setCurrentOverlayId={setCurrentOverlayId}
                firstItemRefresh={firstItemRefresh} refreshClick={refreshClick} isUser={!!user}
                updateUser={appendUserArray} darkMode={prefersDarkMode} leftHanded={leftHanded}
                enableQuickScroll={enableQuickScroll} autoRefresh={autoRefresh}
                enableFirstItem={enableShowFirstItem}
                showQuickScroll={showQuickScroll} openRef={openRef} alwaysShow={alwaysShow} preload={preload}
                imageSelections={listingImageSelections}
                sellersDisabled={sellersDisabled} countriesDisabled={countriesDisabled}
                handlePreLoad={handlePreLoad} handleToggle={handleToggle}
                setLoading={setLoading}
                items={items} sort={sort} recentItems={recentItems} filterSellers={filterSellers}
                filterCountries={filterCountries}
                blockedSellers={blockedSellers} blockedCountries={blockedCountries} />
            <NoItems setSearchParams={setSearchParams}
                showHome={!loadingUserData && !searchParams.has('keywords')} loading={loading}
                noUser={!user}
                darkMode={prefersDarkMode}
                total={totals} items={items.length} handleToggle={handleToggle}
                handlePreload={handlePreLoad} preload={preload} currentItem={currentItem}
                recentItems={recentItems}
                currentOverlayId={currentOverlayId} setCurrentOverlayId={setCurrentOverlayId}
                currencyApi={currencyApi} auth={auth}
            />
        </>
    )
};